import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import BackendApiClient from '../../services/api/BackendApiClient';
import {
  ErrorHandlerInterface,
  GenericOrder,
  GiftCard,
  GiftCardBasketItem,
  GiftCardOrder,
} from '../../services/exports/Interfaces';
import { AxiosHeaders } from 'axios';
import { PaymentIntent } from '@stripe/stripe-js';
import { ORDER_PAYMENT_STATES, ORDER_PAYMENT_STATUSES } from '../../services/exports/Constants';

interface GetGiftCardDto {
  companyId: number;
  code: string;
}

interface GiftCardsCheckoutDto extends GiftCardBasketItem {
  companyId: number;
  errorHandlers?: ErrorHandlerInterface;
  headers?: AxiosHeaders;
}

export interface GiftCardsCheckoutResponse {
  payment_intent?: PaymentIntent;
  order: GenericOrder;
}

export interface GiftCardPaymentStatusResponse {
  id: string;
  payment_status: ORDER_PAYMENT_STATUSES;
  /**
   * Derived discriminator from the shared OrderPaymentStatusResource. A failed gift-card payment
   * keeps `payment_status` at `pending` forever (no terminal failure status exists), so this is the
   * only signal that the backend has decided. Optional to stay tolerant of older payloads.
   */
  payment_state?: ORDER_PAYMENT_STATES;
}

/**
 * The rejected checkout as the storefront reads it: the status, the backend's answer and the OTP
 * header it names. Deliberately not the AxiosError itself, which RTK Query keeps in the action
 * payload and the mutation's state — both of which the Sentry Redux enhancer records — and whose
 * `config.data` is the request body, the payer's CPF on a Pix gift card included.
 */
function checkoutError(failure: any, status: number) {
  const response = failure?.response ?? failure;
  const headers = Object.fromEntries(
    Object.entries(response?.headers ?? {}).filter(([name]) => name.toLowerCase().startsWith('x-otp-')),
  );

  return {
    status,
    data: {
      response: {
        data: response?.data,
        status: response?.status ?? status,
        headers,
      },
    },
  };
}

export const giftCardsApiSlice = createApi({
  baseQuery: fakeBaseQuery(),
  reducerPath: 'giftCardsApiSlice',
  endpoints: (build) => ({
    validateGiftCard: build.query<GiftCard, GetGiftCardDto>({
      queryFn: ({ companyId, ...data }) =>
        BackendApiClient.request({
          method: 'POST',
          url: `/companies/${companyId}/gift-cards/validate`,
          data,
        }).then((res) => {
          if (res.success) {
            return { data: res.response.data, status: res.status };
          }

          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }),
    }),
    getOrderPaymentStatus: build.query<GiftCardPaymentStatusResponse, string>({
      queryFn: (orderId) =>
        BackendApiClient.request({
          method: 'GET',
          url: `/gift-card-orders/${orderId}/payment-status`,
        }).then((res) => {
          if (res.success) {
            return { data: res.response.data.data, status: res.status };
          }

          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }),
    }),
    getGiftCardOrder: build.query<GiftCardOrder, string>({
      queryFn: (orderId) =>
        BackendApiClient.request({
          method: 'GET',
          url: `/gift-card-orders/${orderId}`,
        }).then((res) => {
          if (res.success) {
            return { data: res.response.data.data, status: res.status };
          }

          return {
            error: {
              data: res.response,
              status: res.status,
            },
          };
        }),
    }),
    giftCardsCheckout: build.mutation<GiftCardsCheckoutResponse | GenericOrder, GiftCardsCheckoutDto>({
      queryFn: ({ companyId, errorHandlers, headers, ...data }) =>
        BackendApiClient.request(
          {
            method: 'POST',
            url: `/companies/${companyId}/gift-cards/checkout`,
            data,
            headers,
          },
          null,
          errorHandlers,
        )
          .then((res) => {
            if (res.success) {
              return { data: res.response.data.data, status: res.status, error: undefined };
            }

            return { error: checkoutError(res.response, res.status) };
          })
          .catch((err) => ({ error: checkoutError(err.response, err.status) })),
    }),
  }),
});

export const {
  useLazyValidateGiftCardQuery,
  useGiftCardsCheckoutMutation,
  useGetOrderPaymentStatusQuery,
  useGetGiftCardOrderQuery,
} = giftCardsApiSlice;
